// Composer 범용 검색 제안 축(suggestionsFrom)의 admin 저작 어휘 — 소스 select 옵션 + 저장 정규화.
// 결정: docs/decisions/2026-08-05-composer-suggestion-source-axis.md
//
// ⚠️ 소스 목록 자체는 여기 없다 — `@safetyhub/shared`의 COMPOSER_SUGGESTION_SOURCES가 단일 정본이다
// (저작(admin) ↔ 호스트 어댑터(web)가 같이 아는 경계 계약이라 한쪽에 두면 문자열이 갈라진다).
// 이 파일은 그 정본 위에 얹히는 **저작 UI 로직**(옵션 산출·보존 표기·저장 정규화)만 소유한다.

import {
  COMPOSER_SUGGESTION_SOURCES,
  findComposerSuggestionSource,
  type ComposerSuggestionSourceDecl,
  type RegisteredWidgetWrite,
  type SuggestionsFromConfig,
} from '@safetyhub/shared';

export type SuggestionSourceDecl = ComposerSuggestionSourceDecl;

export const findSuggestionSource = findComposerSuggestionSource;

/** 검색 소스 select 옵션 1건 — preserved=저장값이 선언 밖(호스트에서 제거·오타 등) 보존 표기. */
export interface SuggestionSourceOption {
  value: string;
  label: string;
  preserved?: boolean;
}

/**
 * 소스 select 옵션 — 선언 목록 + 현재값이 선언 밖이면 보존 옵션을 **선두**에 유지(재저장 시 소실
 * 방지 — 콤보 소스·행 필드 select의 "목록에 없음 — 보존" 규약 동형). 빈 값(축 미설정)은 옵션
 * 아님(호출부가 "없음" 항목을 따로 둔다).
 */
export function suggestionSourceOptions(current: string | undefined): SuggestionSourceOption[] {
  const opts = COMPOSER_SUGGESTION_SOURCES.map((s) => ({
    value: s.key,
    label: `${s.label} (${s.key})`,
  }));
  if (current && !opts.some((o) => o.value === current)) {
    return [{ value: current, label: `${current} (목록에 없음 — 보존)`, preserved: true }, ...opts];
  }
  return opts;
}

/** writes 전파 source 후보 — 선언 밖 소스면 빈 배열(WritesMappingEditor가 기존 값은 자체 보존). */
export function suggestionSourceRowFields(
  key: string | undefined,
): { key: string; label: string }[] {
  return findSuggestionSource(key)?.rowFields ?? [];
}

/**
 * 저장 정규화 — 미설정이면 축 자체를 **저장하지 않는다**(undefined 반환, 빈 객체 금지).
 *  - catalogKey 없음/공백 = 축 해제(필터·전파도 함께 폐기)
 *  - filterByRowField 공백 = 키 제거
 *  - writes 0건 = 키 제거(빈 배열 저장 금지)
 *  - requireSelection false = 키 제거(기본값이라 JSON 미방출 — 접기·편집 진입 축 규약 동형)
 */
export function normalizeSuggestionsFrom(
  cfg: SuggestionsFromConfig | undefined,
): SuggestionsFromConfig | undefined {
  const catalogKey = cfg?.catalogKey?.trim() ?? '';
  if (!catalogKey) return undefined;
  const next: SuggestionsFromConfig = { catalogKey };
  const filter = cfg?.filterByRowField?.trim();
  if (filter) next.filterByRowField = filter;
  const writes = cfg?.writes ?? [];
  if (writes.length > 0) next.writes = writes;
  if (cfg?.requireSelection) next.requireSelection = true;
  return next;
}

/**
 * 부분 갱신 — 컨트롤 조작 1회분을 현재 config에 얹어 정규화한다.
 * **소스 교체 시 writes 폐기**: source 경로는 그 소스의 행 형태 전용이라 다른 소스에서 의미가 없다
 * (콤보 setSource가 sourceFieldMaps를 폐기하는 규약 동형). filterByRowField는 소스가 아니라 **폼의
 * 행/본문 필드**를 가리키므로 소스가 바뀌어도 유지한다(호스트가 모르는 키면 필터 없이 검색될 뿐).
 */
export function patchSuggestionsFrom(
  current: SuggestionsFromConfig | undefined,
  patch: {
    catalogKey?: string;
    filterByRowField?: string | undefined;
    writes?: RegisteredWidgetWrite[];
    requireSelection?: boolean;
  },
): SuggestionsFromConfig | undefined {
  const nextKey = (patch.catalogKey ?? current?.catalogKey ?? '').trim();
  const sourceChanged = patch.catalogKey !== undefined && nextKey !== (current?.catalogKey ?? '');
  return normalizeSuggestionsFrom({
    catalogKey: nextKey,
    filterByRowField:
      patch.filterByRowField !== undefined ? patch.filterByRowField : current?.filterByRowField,
    writes: patch.writes !== undefined ? patch.writes : sourceChanged ? [] : current?.writes,
    // 선택 강제는 **칸의 성질**이라 소스를 바꿔도 유지한다(writes와 달리 소스 형태에 의존하지 않음).
    requireSelection:
      patch.requireSelection !== undefined ? patch.requireSelection : current?.requireSelection,
  });
}
